import { useMemo, useState } from 'react';
import { useData } from '../lib/data';
import { useSelection } from '../lib/selection';
import { supabase } from '../lib/supabase';
import { READONLY } from '../lib/readonly';
import { useToast } from '../lib/toast';
import { updateTaskWithRollback } from '../lib/taskWrites';
import type { Task, TaskPriority, TaskStatus } from '../lib/database.types';
import { DueChip } from '../components/DueChip';

const PRIORITY_WEIGHT: Record<TaskPriority, number> = { critical: 0, high: 1, medium: 2, low: 3 };
const ACTIVE = new Set(['pending', 'in_progress', 'blocked']);

type PriorityFilter = 'all' | TaskPriority;
type StatusFilter = 'all' | TaskStatus;

export function MyTasks() {
  const { tasks, projectBySlug, clientBySlug, clients } = useData();
  const { select } = useSelection();

  const [clientFilter, setClientFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const mine = useMemo(
    () =>
      tasks
        .filter(
          (t) =>
            t.parent_task_id === null &&
            (statusFilter === 'all' ? ACTIVE.has(t.status) : t.status === statusFilter) &&
            (t.assigned_to === 'me' ||
              (!t.assigned_to && (t.execution_target === 'me' || !t.execution_target))),
        )
        .filter((t) => priorityFilter === 'all' || t.priority === priorityFilter)
        .filter((t) => {
          if (clientFilter === 'all') return true;
          const project = projectBySlug[t.project_slug];
          return project?.client_slug === clientFilter;
        })
        .sort((a, b) => {
          if (a.due_at && b.due_at) return a.due_at.localeCompare(b.due_at);
          if (a.due_at) return -1;
          if (b.due_at) return 1;
          const pw = PRIORITY_WEIGHT[a.priority] - PRIORITY_WEIGHT[b.priority];
          if (pw !== 0) return pw;
          return a.created_at.localeCompare(b.created_at);
        }),
    [tasks, statusFilter, priorityFilter, clientFilter, projectBySlug],
  );

  const buckets = useMemo(() => {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    const overdue: Task[] = [];
    const today: Task[] = [];
    const soon: Task[] = [];
    const later: Task[] = [];
    const undated: Task[] = [];
    for (const t of mine) {
      if (!t.due_at) {
        undated.push(t);
        continue;
      }
      const diff = Math.floor(
        (new Date(t.due_at).getTime() - midnight.getTime()) / 86_400_000,
      );
      if (diff < 0) overdue.push(t);
      else if (diff === 0) today.push(t);
      else if (diff <= 7) soon.push(t);
      else later.push(t);
    }
    return { overdue, today, soon, later, undated };
  }, [mine]);

  const sections: Array<[string, string, Task[]]> = [
    ['OVERDUE', 'text-red', buckets.overdue],
    ['TODAY', 'text-amberHi', buckets.today],
    ['THIS WEEK', 'text-blue', buckets.soon],
    ['LATER', 'text-muted', buckets.later],
    ['NO DUE DATE', 'text-dim', buckets.undated],
  ];

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-5xl p-8 space-y-6">
        <header>
          <h1 className="text-3xl font-semibold tracking-tight">My Tasks</h1>
          <p className="text-sm text-muted font-mono mt-1">
            {mine.length} tasks · me
            {(clientFilter !== 'all' || priorityFilter !== 'all' || statusFilter !== 'all') && (
              <>
                {' '}
                ·{' '}
                <button
                  onClick={() => {
                    setClientFilter('all');
                    setPriorityFilter('all');
                    setStatusFilter('all');
                  }}
                  className="text-amberHi hover:text-white underline"
                >
                  clear filters
                </button>
              </>
            )}
          </p>
        </header>

        <div className="panel p-3 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono text-dim uppercase tracking-widest">
            filter
          </span>
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="px-3 py-2 bg-panelHi border border-border rounded text-xs font-mono min-h-[36px]"
          >
            <option value="all">all companies</option>
            {clients.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.display_name}
              </option>
            ))}
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as PriorityFilter)}
            className="px-3 py-2 bg-panelHi border border-border rounded text-xs font-mono min-h-[36px]"
          >
            <option value="all">all priorities</option>
            <option value="critical">critical</option>
            <option value="high">high</option>
            <option value="medium">medium</option>
            <option value="low">low</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="px-3 py-2 bg-panelHi border border-border rounded text-xs font-mono min-h-[36px]"
          >
            <option value="all">active (pending+in progress+blocked)</option>
            <option value="pending">pending</option>
            <option value="in_progress">in progress</option>
            <option value="blocked">blocked</option>
            <option value="completed">completed</option>
          </select>
        </div>

        {sections.map(([label, cls, ts]) =>
          ts.length > 0 ? (
            <section key={label} className="space-y-2">
              <h2 className={`text-xs font-mono tracking-widest uppercase ${cls}`}>
                {label} <span className="text-dim">· {ts.length}</span>
              </h2>
              <div className="space-y-1.5">
                {ts.map((t) => (
                  <MineTaskRow
                    key={t.id}
                    task={t}
                    projectName={projectBySlug[t.project_slug]?.name}
                    clientName={
                      projectBySlug[t.project_slug]?.client_slug
                        ? clientBySlug[projectBySlug[t.project_slug].client_slug!]?.display_name
                        : undefined
                    }
                    onOpen={() => select({ type: 'task', id: t.id })}
                  />
                ))}
              </div>
            </section>
          ) : null,
        )}

        {mine.length === 0 && (
          <div className="panel p-10 text-center text-dim font-mono text-sm">
            nothing on your plate
          </div>
        )}
      </div>
    </div>
  );
}

function MineTaskRow({
  task,
  projectName,
  clientName,
  onOpen,
}: {
  task: Task;
  projectName?: string;
  clientName?: string;
  onOpen: () => void;
}) {
  const { patchTaskLocal } = useData();
  const { push } = useToast();
  const [busy, setBusy] = useState(false);

  const complete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (READONLY) return;
    setBusy(true);
    const now = new Date().toISOString();
    await updateTaskWithRollback({
      task,
      patch: { status: 'completed', completed_at: now },
      patchTaskLocal,
      push,
      persist: async () => {
        const { data, error } = await supabase
          .from('tb_tasks')
          .update({ status: 'completed', completed_at: now })
          .eq('id', task.id)
          .select()
          .single();
        return { data: (data as Task | null) ?? null, error };
      },
      successToast: {
        kind: 'success',
        title: 'Task completed',
        body: task.title.slice(0, 60),
        ttlMs: 2500,
      },
      errorTitle: 'Failed to complete task',
    });
    setBusy(false);
  };

  const isDone = task.status === 'completed';

  return (
    <div
      className="panel p-3 flex items-center gap-3 hover:border-borderHi
                 hover:bg-panelHi/40 transition-colors"
    >
      <button
        onClick={complete}
        disabled={busy || isDone}
        aria-label="mark complete"
        className="check-hit"
        title="mark complete"
      >
        {isDone ? (
          <span className="check-dot-done">✓</span>
        ) : (
          <span className="check-dot" />
        )}
      </button>
      <button onClick={onOpen} className="flex-1 min-w-0 text-left cursor-pointer">
        <div
          className={
            'text-sm font-medium truncate ' + (isDone ? 'line-through text-dim' : '')
          }
        >
          {task.title}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted font-mono mt-1 flex-wrap">
          {clientName && <span className="text-amberHi">{clientName}</span>}
          {clientName && <span className="text-dim">·</span>}
          <span>{projectName ?? task.project_slug}</span>
          <span className="text-dim">·</span>
          <span className={`chip chip-${task.priority}`}>{task.priority}</span>
          <DueChip dueAt={task.due_at} />
        </div>
      </button>
    </div>
  );
}
